import { getFilterableField } from '../config/filterable-fields';
import type { Contact } from '../types/contacts.types';
import { toContactStringValue } from './contact-attributes';

const API_SOURCE_VALUES = new Set(['api', 'crmapi', 'eshopapi']);

const UUID_PATTERN =
  /^[0-9a-f]{8}[-\s]?[0-9a-f]{4}[-\s]?[0-9a-f]{4}[-\s]?[0-9a-f]{4}[-\s]?[0-9a-f]{12}$/i;
const OBJECT_ID_PATTERN = /^[0-9a-f]{24}$/i;

const isOpaqueSourceClientId = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return true;

  const compact = trimmed.replace(/[\s-]/g, '');
  if (UUID_PATTERN.test(trimmed) || UUID_PATTERN.test(compact)) return true;
  if (OBJECT_ID_PATTERN.test(compact)) return true;
  if (/^[0-9a-f]{32}$/i.test(compact)) return true;

  return false;
};

const formatSourceClientLabel = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed || isOpaqueSourceClientId(trimmed)) return undefined;

  const lower = trimmed.toLowerCase();
  if (lower === 'crm') return 'CRM';
  if (lower === 'eshop' || lower === 'e-shop') return 'E-shop';

  return trimmed
    .split(/[\s._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const getContactSourcePrimaryLabel = (source: string): string => {
  const trimmed = source.trim();
  const lower = trimmed.toLowerCase();

  if (API_SOURCE_VALUES.has(lower)) return 'API';
  if (lower === 'manual') return 'Manual';
  if (lower === 'import' || lower === 'csvimport') return 'Import';

  const option = getFilterableField('source')?.options?.find(
    (entry) => entry.value.toLowerCase() === lower
  );
  if (option) {
    if (option.value === 'CrmApi' || option.value === 'EshopApi') return 'API';
    if (option.value === 'CsvImport') return 'Import';
    if (option.value === 'Manual') return 'Manual';
    return option.label;
  }

  return trimmed;
};

export const formatContactSourceLabel = (contact?: Contact | null): string | undefined => {
  const rawSource = toContactStringValue(contact?.Source);
  if (!rawSource) return undefined;

  const primary = getContactSourcePrimaryLabel(rawSource);
  const sourceClient = toContactStringValue(contact?.SourceClient);
  // An import's client is the uploaded file's name — shown as the user named it.
  const clientLabel = !sourceClient
    ? undefined
    : primary === 'Import'
      ? sourceClient.trim() || undefined
      : formatSourceClientLabel(sourceClient);

  if (clientLabel) {
    return `${primary} · ${clientLabel}`;
  }

  return primary;
};
